<script lang="ts" setup>
import {
   ContractTemplateFieldName,
   ContractTemplateFieldNameKey,
} from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { ContractTemplateField } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { CustomerAddressTypeKey } from '@/core/collections/contracts-api/customers/customer-address-types/customer-address-type.interface';
import { CustomerAddress } from '@/core/collections/contracts-api/customers/customer-addresses/customer-address.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import CustomerAddressDropdownLabel from '@/core/components/form-elements/CustomerAddressDropdownLabel.vue';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import { formatCustomerAddressSingleLine } from '@/core/helpers/customer-address-format.helper';
import { FormType } from '@/core/models/form-type.enum';
import store from '@/core/store';
import { NxtFormSection } from '@nxtpeople/nxt-styles';
import { PropType, computed, inject, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractTemplateWizardField from './ContractTemplateWizardField.vue';
import { useContractTemplate } from './useContractTemplate';
import { useContractTemplateKeys } from './useContractTemplateKeys';

const { t, locale } = useI18n();
const { ADDRESS_FIELD_KEYS, FUNCTION_DATA_FIELD_KEYS } = useContractTemplateKeys();
const contractTemplate = useContractTemplate();

const customer = inject('customer', ref<Customer>());
const company = computed(() => customer.value?.company);

//#region Props
const emits = defineEmits<{ (e: 'onUpdateField', payload: { fieldNameId: number; key: string; value: any }): void }>();
const props = defineProps({
   fields: { type: Array as PropType<Array<ContractTemplateField>>, default: () => [] },
   fieldNames: {
      type: Array as PropType<Array<ContractTemplateFieldName>>,
      default: () => [],
   },
   loading: { type: Boolean, default: false },
   errors: { type: Object, default: () => ({}) },
});

const { fields, fieldNames } = toRefs(props);
//#endregion

//#region Address
const selectedCustomerAddress = ref<CustomerAddress>();

const customerAddresses = computed(() => {
   return (customer.value?.addresses ?? [])
      .map((address) => ({
         ...address,
         address_short: formatCustomerAddressSingleLine(address, locale.value),
      }))
      .sort((a, b) => {
         const isAHeadquarters = a.customer_address_type?.key === CustomerAddressTypeKey.Headquarters;
         const isBHeadquarters = b.customer_address_type?.key === CustomerAddressTypeKey.Headquarters;
         if (isAHeadquarters === isBHeadquarters) return 0;
         return isAHeadquarters ? -1 : 1;
      });
});

function setAddress(address?: any) {
   emits('onUpdateField', {
      fieldNameId: fieldNames.value?.find((fn) => fn.key === ContractTemplateFieldNameKey.EmploymentStreet)!.id,
      key: 'value',
      value: address?.street,
   });
   emits('onUpdateField', {
      fieldNameId: fieldNames.value?.find((fn) => fn.key === ContractTemplateFieldNameKey.EmploymentHouseNumber)!.id,
      key: 'value',
      value: address?.house_number,
   });
   emits('onUpdateField', {
      fieldNameId: fieldNames.value?.find((fn) => fn.key === ContractTemplateFieldNameKey.EmploymentBox)!.id,
      key: 'value',
      value: address?.box,
   });
   emits('onUpdateField', {
      fieldNameId: fieldNames.value?.find((fn) => fn.key === ContractTemplateFieldNameKey.EmploymentCityId)!.id,
      key: 'value',
      value: address?.city_id,
   });
   emits('onUpdateField', {
      fieldNameId: fieldNames.value?.find((fn) => fn.key === ContractTemplateFieldNameKey.EmploymentCityId)!.id,
      key: 'selected_value',
      value: address?.city,
   });
}

watch(selectedCustomerAddress, setAddress);
//#endregion

//#region Employment Motive
const employmentMotives = computed(() => customer.value?.allowed_employment_motives || []);
const employmentMotiveField = computed(() => fields.value.find((field) => field.field_key === 'employment_motive_id'));
const employmentMotive = computed(() => employmentMotives.value.find((em) => em.id === employmentMotiveField.value?.value));

const allowedEmploymentMotiveIds = computed(() => new Set(employmentMotives.value.map((motive) => motive.id)));
watch(allowedEmploymentMotiveIds, clearDisallowedEmploymentMotive, { immediate: true });

function clearDisallowedEmploymentMotive(allowedIds: Set<number>) {
   const field = employmentMotiveField.value;
   if (!field?.value || allowedIds.has(field.value)) return;

   emits('onUpdateField', { fieldNameId: field.contract_template_field_name_id, key: 'value', value: undefined });
}

const showTerminationAgreement = computed(() => employmentMotive.value?.key === 'replacement_termination_agreement');
const showSuspensionAgreement = computed(() => employmentMotive.value?.key === 'replacement_suspension_agreement');

const replacementMotiveFieldName = computed(() => {
   const fieldName = fieldNames.value.find((fn) => fn.key === 'replacement_motive_id');
   if (!fieldName) return undefined;
   return {
      ...fieldName,
      options: showTerminationAgreement.value
         ? store.state.replacementMotive?.terminationMotives || []
         : showSuspensionAgreement.value
           ? store.state.replacementMotive?.suspensionMotives || []
           : [],
   };
});
watch(showTerminationAgreement, clearReplacementMotiveId);
watch(showSuspensionAgreement, clearReplacementMotiveId);

function clearReplacementMotiveId(value?: boolean) {
   if (value) return;

   return emits('onUpdateField', { fieldNameId: replacementMotiveFieldName.value!.id, key: 'value', value: undefined });
}
//#endregion

//#region Comment
const commentFieldName = computed(() => fieldNames.value.find((fn) => fn.key === ContractTemplateFieldNameKey.Comment));
//#endregion

//#region Fields
const employmentAddressFields = computed(() =>
   fieldNames.value?.filter((fn) => ADDRESS_FIELD_KEYS.value?.find(({ key }) => fn.key === key)),
);
const employmentStreetFieldName = computed(() =>
   employmentAddressFields.value.find((field) => field.key === ContractTemplateFieldNameKey.EmploymentStreet),
);
const employmentHouseNumberFieldName = computed(() =>
   employmentAddressFields.value.find((field) => field.key === ContractTemplateFieldNameKey.EmploymentHouseNumber),
);
const employmentBoxFieldName = computed(() =>
   employmentAddressFields.value.find((field) => field.key === ContractTemplateFieldNameKey.EmploymentBox),
);
const employmentCityFieldName = computed(() =>
   employmentAddressFields.value.find((field) => field.key === ContractTemplateFieldNameKey.EmploymentCityId),
);
const isTeamOrNightSurchargeIncluded = computed(() => contractTemplate.value.customer?.is_team_or_night_surcharge_included);
const functionDataFields = computed(() =>
   fieldNames.value
      ?.filter((fn) => FUNCTION_DATA_FIELD_KEYS.value?.find(({ key }) => fn.key === key))
      .filter((fn) => fn.key !== ContractTemplateFieldNameKey.ReplacementMotiveId)
      .filter((fn) => (fn.key === ContractTemplateFieldNameKey.SalaryIncludesId ? isTeamOrNightSurchargeIncluded.value : true))
      .sort((fn) => (['employment_motive_id', 'replacement_motive_id'].includes(fn.key) ? 1 : -1)),
);

function getField(fieldNameId: number) {
   return fields.value.find((field) => field.contract_template_field_name_id === fieldNameId);
}
//#endregion
</script>

<template>
   <div class="contract-template-wizard-detail-data">
      <NxtFormSection :title="t('generic.address')">
         <div class="contract-template-wizard-detail-data__section -address">
            <div class="row mb-3">
               <Dropdown
                  :model-value="selectedCustomerAddress?.id"
                  :class="'col-8'"
                  :name="'employment_address_type'"
                  :label="t('generic.choose-address')"
                  :options="customerAddresses"
                  :disabled="loading"
                  :errors="errors"
                  :display-key="'address_short'"
                  :unique-key="'id'"
                  :emit-object="true"
                  @update:model-value="selectedCustomerAddress = $event"
               >
                  <template #option="{ option }">
                     <CustomerAddressDropdownLabel :address="option" />
                  </template>
                  <template #displayOption="{ option }">
                     <CustomerAddressDropdownLabel :address="option" :show-address-type="false" />
                  </template>
               </Dropdown>
            </div>

            <div class="row mb-3">
               <ContractTemplateWizardField
                  v-if="employmentStreetFieldName"
                  :class="'col-5'"
                  :errors="errors"
                  :disabled="loading"
                  :field-name="employmentStreetFieldName"
                  :field="getField(employmentStreetFieldName.id)"
                  :hide-calculated-values="!employmentStreetFieldName.allows_conditions"
                  @on-update-field="emits('onUpdateField', $event)"
               />
               <ContractTemplateWizardField
                  v-if="employmentHouseNumberFieldName"
                  :class="'col-3'"
                  :errors="errors"
                  :disabled="loading"
                  :field-name="employmentHouseNumberFieldName"
                  :field="getField(employmentHouseNumberFieldName.id)"
                  :hide-calculated-values="!employmentHouseNumberFieldName.allows_conditions"
                  @on-update-field="emits('onUpdateField', $event)"
               />
               <ContractTemplateWizardField
                  v-if="employmentBoxFieldName"
                  :class="'col-3'"
                  :errors="errors"
                  :disabled="loading"
                  :field-name="employmentBoxFieldName"
                  :field="getField(employmentBoxFieldName.id)"
                  :hide-calculated-values="!employmentBoxFieldName.allows_conditions"
                  @on-update-field="emits('onUpdateField', $event)"
               />
            </div>
            <div class="row">
               <ContractTemplateWizardField
                  v-if="employmentCityFieldName"
                  :class="'col-6'"
                  :errors="errors"
                  :disabled="loading"
                  :field-name="employmentCityFieldName"
                  :field="getField(employmentCityFieldName.id)"
                  :hide-calculated-values="!employmentCityFieldName.allows_conditions"
                  @on-update-field="emits('onUpdateField', $event)"
               />
            </div>
         </div>
      </NxtFormSection>

      <NxtFormSection :title="t('contracts.function-data', 2)">
         <div class="contract-template-wizard-detail-data__section">
            <ContractTemplateWizardField
               v-for="fieldName in functionDataFields"
               :key="fieldName.key"
               :errors="errors"
               :mode="'light'"
               :disabled="loading"
               :field-name="fieldName"
               :field="getField(fieldName.id)"
               :hide-calculated-values="!fieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />

            <ContractTemplateWizardField
               v-if="replacementMotiveFieldName && showTerminationAgreement"
               :errors="errors"
               :disabled="loading"
               :field-name="replacementMotiveFieldName"
               :mode="'light'"
               :field="getField(replacementMotiveFieldName.id)"
               :custom-options-url="'replacementMotive/getTerminationReasons'"
               :hide-calculated-values="!replacementMotiveFieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />

            <ContractTemplateWizardField
               v-if="replacementMotiveFieldName && showSuspensionAgreement"
               :errors="errors"
               :disabled="loading"
               :field-name="replacementMotiveFieldName"
               :field="getField(replacementMotiveFieldName.id)"
               :mode="'light'"
               :custom-options-url="'replacementMotive/getSuspensionReasons'"
               :hide-calculated-values="!replacementMotiveFieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />
         </div>
      </NxtFormSection>

      <NxtFormSection :title="t('generic.comment')">
         <ContractTemplateWizardField
            v-if="commentFieldName"
            :errors="errors"
            :mode="'light'"
            :disabled="loading"
            :field-name="{ ...commentFieldName, form_type: FormType.TextArea }"
            :field="getField(commentFieldName.id)"
            :hide-calculated-values="!commentFieldName.allows_conditions"
            @on-update-field="emits('onUpdateField', $event)"
         />
      </NxtFormSection>
   </div>
</template>

<style lang="scss" scoped>
.contract-template-wizard-detail-data {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}
.contract-template-wizard-detail-data__section {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: var(--nxt-gutter);
   margin-right: toRem(250);

   &.-address {
      display: block;
      margin-right: toRem(250);
   }
}
</style>
